/**
 * @file index.tsx
 *
 * @fileoverview Job search page. Performs the graphql query and renders a job search
 * as a plit screen view.
 */
import dynamic from 'next/dynamic';
import React, { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { JobSearchView } from '~/jobiqo/components/JobSearch/SplitView/View';
import { getJobSearchDimensions } from '~/jobiqo/components/JobSearch/utils/job_search_analytics';
import { useAutoSelectJob } from '~/jobiqo/components/JobSearch/hooks/useAutoSelectJob';
import { useSearch } from '~/jobiqo/components/Search/hooks/useSearch';
import { getSearchLabel } from '~/jobiqo/components/Search/search-title';
import { getGeoInputFromUrl } from '~/jobiqo/components/Search/utils/searchHelpers';
import { PathContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { HeaderContext, HeaderContextInterface } from '~/jobiqo/context/header';
import { LayoutContext, LayoutContextInterface } from '~/jobiqo/context/layout';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { useJobSearch } from '~/jobiqo/context/jobSearch';
import {
  analyticsDispatch,
  EVENTS,
  PAGE_TYPES,
  PageType,
} from '~/jobiqo/lib/analytics';
import { PathContextInterface } from '~/jobiqo/models/app';
import { FacetConfig } from '~/jobiqo/models/search';
import { Job, Metatag } from '~/jobiqo/types/types';

type JobSearchContainerSize = 'small' | 'medium' | 'large' | 'xLarge' | 'unset';

const JobSearchError = dynamic(
  import('~/jobiqo/components/JobSearch/SplitView/JobSearchError'),
  { ssr: false },
);
const JobSearchLoading = dynamic(
  import('~/jobiqo/components/JobSearch/SplitView/JobSearchLoading'),
  {
    ssr: false,
  },
);

/**
 * Finds a job in a provided array that matches provided job id.
 *
 * @param {Job[]} jobs
 *  Array of jobs to search.
 * @param {string | null | undefined} currentJobId
 *  Id of job to find.
 *
 * @returns {Job | null}
 *  The job if found, null otherwise.
 */
export const getJobFromList = (
  jobs: Job[],
  currentJobId: string | null | undefined,
): Job | null => {
  if (!jobs || !currentJobId) {
    return null;
  }

  const jobToReturn =
    jobs &&
    jobs.filter((job: Job) => {
      return job.id === Number(currentJobId);
    })[0];

  return jobToReturn;
};

interface JobSearchSplitProps {
  /**
   * The current theme.
   */
  theme?: any;
  querystring: any;
  loading: boolean;
  error: boolean;
  data: any;
  /**
   * Search URL to use for redirects and canonical search URLs.
   */
  searchUrl?: string;
  /**
   * Override facets config. Used by mini-site search to disable facets.
   */
  jobSearchFacetsConfig?: FacetConfig[];
  /**
   * Render only fulltext search controls.
   */
  fullTextOnly?: boolean;
  /**
   * Render without wrapping the split view in the default Layout.
   */
  renderLayout?: boolean;
  /**
   * Hide the site header when rendering the default Layout.
   */
  hideHeader?: boolean;
  /**
   * Force context-based split-view routing outside the default jobs page.
   */
  shallowJobSearch?: boolean;
  /**
   * Wrap only the search form area in a box.
   */
  boxSearchForm?: boolean;
  /**
   * Container size for the search form and split view results.
   */
  containerSize?: JobSearchContainerSize;
  /**
   * Disable standalone cover-image overlap on job detail cards.
   */
  disableJobDetailCoverOverlap?: boolean;
  /**
   * Hide breadcrumbs above job detail cards.
   */
  hideJobDetailBreadcrumbs?: boolean;
  /**
   * Analytics page context for search/detail events emitted from this split view.
   */
  pageType?: PageType;
  /**
   * Optional organization name for search analytics context (e.g. minisite).
   */
  analyticsOrganizationName?: string;
}

export const JobSearchSplit = ({
  querystring,
  loading,
  error,
  data,
  searchUrl,
  jobSearchFacetsConfig: jobSearchFacetsConfigOverride,
  fullTextOnly = false,
  renderLayout = true,
  hideHeader = false,
  shallowJobSearch = false,
  boxSearchForm = false,
  containerSize,
  disableJobDetailCoverOverlap = false,
  hideJobDetailBreadcrumbs = false,
  pageType = PAGE_TYPES.JOB_SEARCH,
  analyticsOrganizationName,
}: JobSearchSplitProps) => {
  const pathContext: PathContextInterface = useContext(PathContext);
  const userContext: UserContextInterface = useContext(UserContext);
  const headerContext: HeaderContextInterface = useContext(HeaderContext);
  const { JOBS } = useFrontendConfig();
  const {
    isMobile,
    setMobileSearchFiltersOpened,
  }: LayoutContextInterface = useContext(LayoutContext);
  const {
    currentJobId,
    currentJob,
    refetchJob,
    setCurrentJobId,
  } = useJobSearch();
  const intl = useIntl();
  const JOBS_ITEMS_PER_PAGE = JOBS?.JOBS_LISTING_NUMBER;
  const CLEAN_FACETS_NUMBER = JOBS?.SEO?.CLEAN_FACETS_NUMBER;
  const CLEAN_FACETS_ORDER = JOBS?.SEO?.CLEAN_FACETS_ORDER;
  const JOBS_PAGE = JOBS?.JOBS_PAGE;
  const searchPageUrl = searchUrl ?? JOBS_PAGE;
  const NOFOLLOW_EXTERNAL_DESCRIPTION_LINKS =
    JOBS?.DETAIL_NOFOLLOW_EXTERNAL_DESCRIPTION_LINKS ?? false;
  const PLAINTEXT_EXTERNAL_DESCRIPTION_LINKS =
    JOBS?.DETAIL_PLAINTEXT_EXTERNAL_DESCRIPTION_LINKS ?? false;
  const jobSearchFacetsConfig: FacetConfig[] =
    jobSearchFacetsConfigOverride ?? Object.values(JOBS?.FACETS ?? {});

  const {
    getFilterSelectedItems,
    toggleFilter,
    getFilterSelectedFacets,
  } = useSearch({
    searchUrl: searchPageUrl,
    cleanFacetsNumber: CLEAN_FACETS_NUMBER,
    cleanFacetsOrder: CLEAN_FACETS_ORDER,
  });

  if (error) {
    return <JobSearchError jobsPage={JOBS_PAGE} />;
  }

  // Returns all jobs data, including facets.
  let jobs: any = null;
  let radiusSearch: boolean = true;
  if (data) {
    jobs = data.jobs;
    if (jobs.hasOwnProperty('radius_search')) {
      radiusSearch = jobs.radius_search;
    }
  }
  const firstJobId = jobs?.pages?.[0]?.id ? String(jobs.pages[0].id) : null;
  const explicitJobId = querystring?.jobId ? String(querystring.jobId) : null;
  const { jobId: _jobId, backfillid: _backfillid, ...restQuerystring } =
    querystring ?? {};
  // Serialize the search context (filters, search terms) without job selection.
  // Used to detect when the search itself changes (not just the selected job).
  // When this changes, auto-selected jobs are cleared so the new search can auto-select its first result.
  const autoSelectSearchKey = JSON.stringify(restQuerystring);

  useAutoSelectJob({
    isMobile,
    explicitJobId,
    currentJobId,
    firstJobId,
    autoSelectSearchKey,
    setCurrentJobId,
  });

  // Refetch job when jobs list changes (e.g., filters applied).
  useEffect(() => {
    if (currentJobId) {
      refetchJob();
    }
  }, [currentJobId, refetchJob]);

  if (isMobile && !currentJobId) {
    headerContext.handleSearchClick = () => {
      setMobileSearchFiltersOpened(true);
    };
  }

  useEffect(() => {
    // Trigger GA event only if we are not selecting a certain job,
    // but rather changing filters or searching.
    if (jobs && !currentJobId) {
      // Dispatch job search event.
      analyticsDispatch(
        EVENTS.JOB_SEARCH,
        getJobSearchDimensions(
          userContext.user,
          jobs,
          jobs?.seoSearchContent?.title ??
            jobs?.seoSearchContent?.subtitle ??
            getSearchLabel({
              metadata: jobs.metatags as Metatag[],
              type: 'job',
              amount: jobs.result_count ?? 0,
              intl,
            }),
          jobs.facets ? getFilterSelectedFacets(querystring, jobs.facets) : [],
          getGeoInputFromUrl(querystring).label ?? '',
          querystring.search,
          pageType,
          analyticsOrganizationName,
        ),
      );
    }
  }, [querystring, currentJobId]);

  // If there are jobs and query is loading we don't want to show
  // a loading placeholder, rather just keep the existing jobs and update
  // transparently.
  if (!jobs && loading) {
    return <JobSearchLoading />;
  }

  return (
    <>
      <JobSearchView
        jobs={jobs}
        radiusSearch={radiusSearch}
        pathname={pathContext.pathname}
        path={pathContext.path}
        querystring={querystring}
        jobsPage={searchPageUrl}
        itemsPerPage={JOBS_ITEMS_PER_PAGE}
        currentJob={currentJob}
        currentJobId={currentJobId || undefined}
        isMobile={isMobile}
        user={userContext.user}
        toggleFilter={toggleFilter}
        getFilterSelectedItems={getFilterSelectedItems}
        jobSearchFacetsConfig={jobSearchFacetsConfig}
        getFilterSelectedFacets={getFilterSelectedFacets}
        noFollowExternalLinks={NOFOLLOW_EXTERNAL_DESCRIPTION_LINKS}
        plainTextExternalLinks={PLAINTEXT_EXTERNAL_DESCRIPTION_LINKS}
        fullTextOnly={fullTextOnly}
        renderLayout={renderLayout}
        hideHeader={hideHeader}
        shallowJobSearch={shallowJobSearch}
        boxSearchForm={boxSearchForm}
        containerSize={containerSize}
        disableJobDetailCoverOverlap={disableJobDetailCoverOverlap}
        hideJobDetailBreadcrumbs={hideJobDetailBreadcrumbs}
        pageType={pageType}
      />
    </>
  );
};
